<template>
	<div
		v-if="colorSwatches && colorSwatches.length > 0"
		class="relative flex gap-1 items-center mt-2 flex-wrap"
		@mouseenter.stop
		@mouseleave.stop
	>
		<NuxtLink
			v-for="swatch in visibleSwatches"
			:key="swatch.valueId"
			class="inline-block"
			:to="getProductLink(swatch.variant)"
			:aria-label="swatch.name"
		>
			<div
				class="border cursor-pointer transition-all duration-200 relative h-[18px] w-[18px]"
				:class="[
					hoveredColorValueId === swatch.valueId
						? 'ring-1 ring-offset-2 ring-gray-super-dark border-2 border-gray-super-dark'
						: 'border-gray-darker',
					currentColorValueId === swatch.valueId
						? 'border-2 border-gray-super-dark'
						: ''
				]"
				:style="{
					backgroundColor: swatch.colorCode || '#ccc'
				}"
				@mouseenter.stop="$emit('update:hoveredColorValueId', swatch.valueId)"
				@mouseleave.stop="$emit('update:hoveredColorValueId', null)"
			>
				<div
					v-if="currentColorValueId === swatch.valueId"
					class="absolute -bottom-2 left-0 right-0 h-0.5"
				/>
				<div
					v-if="hoveredColorValueId === swatch.valueId && swatch.name"
					class="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-4 py-2 text-sm border shadow-sm whitespace-nowrap z-10 bg-secondary"
				>
					{{ swatch.name }}
				</div>
			</div>
		</NuxtLink>
		<span
			v-if="remainingCount > 0"
			class="text-sm text-gray-500 leading-[18px]"
		>
			+{{ remainingCount }}
		</span>
	</div>
</template>

<script setup lang="ts">
import type { ColorSwatchVariant } from "~/composables/useProductColors";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";

interface Props {
	colorSwatches: ColorSwatchVariant[];
	currentColorValueId: number | null;
	hoveredColorValueId: number | null;
}

defineEmits<{
	(e: "update:hoveredColorValueId", value: number | null): void;
}>();

const { colorSwatches, currentColorValueId, hoveredColorValueId } =
	defineProps<Props>();

const localePath = useLocalePath();

const MAX_VISIBLE_SWATCHES = 5;

const orderedSwatches = computed<ColorSwatchVariant[]>(() => {
	const list = colorSwatches ?? [];
	if (!list.length) return [];
	const currentId = currentColorValueId;
	if (currentId == null) return list;
	const active = list.find((s) => s.valueId === currentId);
	const rest = list.filter((s) => s.valueId !== currentId);
	return active ? [active, ...rest] : list;
});

const visibleSwatches = computed(() =>
	orderedSwatches.value.slice(0, MAX_VISIBLE_SWATCHES)
);

const remainingCount = computed(() =>
	Math.max(0, orderedSwatches.value.length - MAX_VISIBLE_SWATCHES)
);

const getProductLink = (variant: FilteredProductItem) =>
	localePath(`/${variant?.VariantUniqueName || variant?.UniqueName}`);
</script>
